<script setup lang="ts">
import type { InterviewPrep, InterviewPrepPreview, InterviewQuestionKind } from '~/utils/interviewPrep'

/**
 * Interview prep for one posting (utils/interviewPrep.ts), shown whole or as
 * the free preview.
 *
 * Whole (`prep`): the pitch, every question with what it checks and points to
 * answer from, the gaps and what to ask back — on its own page after a
 * purchase, and in the editor for the pack. Preview (`preview`): one question
 * in full, the others by their wording with the answer locked, so the buyer
 * sees exactly what the payment opens.
 *
 * With `unlock`, every locked part is clickable and buys the whole prep
 * (this one on its own where it is on sale, else the pack); the host runs the
 * purchase it emits. As on the Civil Service page, pointing at any locked
 * part lights up all of them at once: one payment opens everything, not the
 * one answer under the pointer.
 */
const props = withDefaults(defineProps<{
  prep?: InterviewPrep | null
  preview?: InterviewPrepPreview | null
  /** Smaller type, for the editor's side panel. */
  compact?: boolean
  /** Every answer open, e.g. for printing; otherwise only the first starts open. */
  expandAll?: boolean
  /** Preview only: what clicking a locked part offers. */
  unlock?: { canBuySingle: boolean; busy?: boolean } | null
  /**
   * Preview only: list about half of the questions and only count the rest,
   * as a further set the purchase opens, so the preview stays short.
   */
  teaseHalf?: boolean
}>(), {
  prep: null,
  preview: null,
  compact: false,
  expandAll: false,
  unlock: null,
  teaseHalf: false,
})

const emit = defineEmits<{
  'buy-single': []
  'buy-pack': []
}>()

const { t } = useI18n()
const { interviewPrepPrice } = usePremiumPlans()

/** Pointing at any locked part lights up all of them. */
const unlockHint = ref(false)

function buyAll() {
  if (!props.unlock || props.unlock.busy) return
  if (props.unlock.canBuySingle) emit('buy-single')
  else emit('buy-pack')
}

/** Makes a locked part a button that buys the whole prep. */
function lockedAttrs() {
  if (!props.unlock) return {}
  return {
    'role': 'button',
    'tabindex': 0,
    'aria-label': t('interviewPrep.list.unlock'),
    'class': ['prep-unlockable', unlockHint.value ? 'prep-unlockable-hint' : ''],
    'onClick': buyAll,
    'onKeydown': (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        buyAll()
      }
    },
    'onMouseenter': () => { unlockHint.value = true },
    'onMouseleave': () => { unlockHint.value = false },
    'onFocus': () => { unlockHint.value = true },
    'onBlur': () => { unlockHint.value = false },
  }
}

/** The lock of a locked part, open while the hint is on. */
const lockIcon = computed(() => (unlockHint.value ? 'bi-unlock-fill !text-accent' : 'bi-lock-fill'))

const role = computed(() => props.prep?.role || props.preview?.role || '')

/** Up to this many in all, every one is listed by its wording, none only counted. */
const TEASE_ALL_UP_TO = 3

/** The locked questions listed by their wording; with `teaseHalf`, half of all questions. */
const shownLocked = computed(() => {
  const preview = props.preview
  if (!preview) return []
  if (!props.teaseHalf) return preview.locked
  const total = preview.locked.length + (preview.sample ? 1 : 0)
  // A short list is listed whole: hiding one of three reads as stingy, not as more to come.
  if (total <= TEASE_ALL_UP_TO) return preview.locked
  const shown = Math.max(0, Math.ceil(total / 2) - (preview.sample ? 1 : 0))
  return preview.locked.slice(0, shown)
})
/** The questions only counted, not listed. */
const hiddenCount = computed(() => (props.preview?.locked.length ?? 0) - shownLocked.value.length)

const kindClass: Record<InterviewQuestionKind, string> = {
  role: 'bg-accent/10 !text-accent-strong dark:!text-accent-soft',
  experience: 'bg-lilac/60 !text-ink dark:bg-gray-700 dark:!text-white',
  gap: 'bg-amber-50 !text-amber-700 dark:bg-amber-900/30 dark:!text-amber-300',
  motivation: 'bg-surface !text-body dark:bg-gray-800 dark:!text-gray-300',
}
</script>

<template>
  <div class="space-y-5" :class="compact ? 'prep-compact' : ''">
    <!-- While a locked part is pointed at: one payment opens all of them -->
    <div v-if="unlock && preview" class="pointer-events-none sticky top-2 z-10 !-mb-[3.25rem] flex h-8 justify-center" aria-hidden="true">
      <div
        class="inline-flex items-center gap-2 rounded-full bg-accent-strong px-4 py-1.5 !text-sm font-semibold !text-white shadow-lift transition duration-300"
        :class="unlockHint ? 'opacity-100' : 'opacity-0 motion-safe:translate-y-1'"
      >
        <i class="bi bi-unlock-fill" />
        {{ unlock.canBuySingle ? t('interviewPrep.view.unlockAll', { price: interviewPrepPrice }) : t('interviewPrep.view.unlockAllPack') }}
      </div>
    </div>

    <div v-if="role" class="prep-eyebrow">{{ t('interviewPrep.view.for', { role }) }}</div>

    <!-- "Tell me about yourself": nearly every interview opens with it -->
    <section v-if="prep?.pitch || preview?.hasPitch">
      <h3 class="prep-heading">{{ t('interviewPrep.view.pitch') }}</h3>
      <p v-if="prep?.pitch || preview?.pitch" class="prep-text whitespace-pre-line">{{ prep?.pitch || preview?.pitch }}</p>
      <template v-else>
        <p class="prep-question prep-locked" v-bind="lockedAttrs()">
          <i class="bi" :class="lockIcon" aria-hidden="true" />
          {{ t('interviewPrep.view.pitchLocked') }}
          <span v-if="unlock" class="prep-included" :class="unlockHint ? 'opacity-100' : 'opacity-0'" aria-hidden="true">{{ t('interviewPrep.view.included') }}</span>
        </p>
      </template>
    </section>

    <section>
      <h3 class="prep-heading">{{ t('interviewPrep.view.questions') }}</h3>

      <!-- Whole: each question opens to its answer; the first one starts open -->
      <div v-if="prep" class="space-y-2">
        <details
          v-for="(q, index) in prep.questions"
          :key="index"
          class="prep-question group"
          :open="expandAll || index === 0">
          <summary class="flex cursor-pointer list-none items-start gap-3">
            <span class="prep-number">{{ index + 1 }}</span>
            <span class="min-w-0 flex-1">
              <span class="prep-kind" :class="kindClass[q.kind]">{{ t(`interviewPrep.kinds.${q.kind}`) }}</span>
              <span class="prep-question-text">{{ q.question }}</span>
            </span>
            <i class="bi bi-chevron-down mt-1 shrink-0 text-muted transition-transform group-open:rotate-180" aria-hidden="true" />
          </summary>
          <div class="mt-3 pl-9">
            <p v-if="q.why" class="prep-why">{{ t('interviewPrep.view.why') }}: {{ q.why }}</p>
            <ul class="prep-answer">
              <li v-for="(point, i) in q.answer" :key="i">{{ point }}</li>
            </ul>
            <p v-if="q.basedOn" class="prep-based">
              <i class="bi bi-file-earmark-person" aria-hidden="true" />
              {{ t('interviewPrep.view.basedOn', { role: q.basedOn }) }}
            </p>
          </div>
        </details>
      </div>

      <!-- Preview: the sample in full, the rest by their wording -->
      <div v-else-if="preview" class="space-y-2">
        <div v-if="preview.sample" class="prep-question !border-accent/50">
          <div class="flex items-start gap-3">
            <span class="prep-number">1</span>
            <span class="min-w-0 flex-1">
              <span class="prep-kind" :class="kindClass[preview.sample.kind]">{{ t(`interviewPrep.kinds.${preview.sample.kind}`) }}</span>
              <span class="prep-question-text">{{ preview.sample.question }}</span>
            </span>
          </div>
          <div class="mt-3 pl-9">
            <p v-if="preview.sample.why" class="prep-why">{{ t('interviewPrep.view.why') }}: {{ preview.sample.why }}</p>
            <ul class="prep-answer">
              <li v-for="(point, i) in preview.sample.answer" :key="i">{{ point }}</li>
            </ul>
            <p v-if="preview.sample.basedOn" class="prep-based">
              <i class="bi bi-file-earmark-person" aria-hidden="true" />
              {{ t('interviewPrep.view.basedOn', { role: preview.sample.basedOn }) }}
            </p>
          </div>
        </div>

        <div v-for="(q, index) in shownLocked" :key="index" class="prep-question" v-bind="lockedAttrs()">
          <div class="flex items-start gap-3">
            <span class="prep-number">{{ index + (preview.sample ? 2 : 1) }}</span>
            <span class="min-w-0 flex-1">
              <span class="prep-kind" :class="kindClass[q.kind]">{{ t(`interviewPrep.kinds.${q.kind}`) }}</span>
              <span class="prep-question-text">{{ q.question }}</span>
              <span class="prep-locked mt-1.5">
                <i class="bi" :class="lockIcon" aria-hidden="true" />
                {{ t('interviewPrep.view.answerLocked') }}
                <span v-if="unlock" class="prep-included" :class="unlockHint ? 'opacity-100' : 'opacity-0'" aria-hidden="true">{{ t('interviewPrep.view.included') }}</span>
              </span>
            </span>
          </div>
        </div>

        <!-- The rest only hinted at: a further set of questions comes with the purchase -->
        <div v-if="hiddenCount > 0" class="prep-question prep-more" v-bind="lockedAttrs()">
          <div class="flex items-start gap-3">
            <span class="prep-number !w-auto min-w-6 px-1.5 !bg-accent-strong !text-white">+{{ hiddenCount }}</span>
            <span class="min-w-0 flex-1">
              <span class="prep-question-text">{{ t('interviewPrep.view.moreQuestions', { n: hiddenCount }) }}</span>
              <span class="prep-locked mt-1.5">
                <i class="bi" :class="lockIcon" aria-hidden="true" />
                {{ t('interviewPrep.view.moreQuestionsText') }}
                <span v-if="unlock" class="prep-included" :class="unlockHint ? 'opacity-100' : 'opacity-0'" aria-hidden="true">{{ t('interviewPrep.view.included') }}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- What the CV does not show, and how to answer it honestly -->
    <section v-if="prep?.gaps.length || preview?.gapRequirements.length">
      <h3 class="prep-heading">{{ t('interviewPrep.view.gaps') }}</h3>
      <p class="prep-why !mb-2">{{ t('interviewPrep.view.gapsIntro') }}</p>
      <ul class="space-y-2">
        <template v-if="prep">
          <li v-for="gap in prep.gaps" :key="gap.requirement" class="prep-question">
            <div class="prep-question-text">{{ gap.requirement }}</div>
            <p class="prep-text mt-1">{{ gap.howToAddress }}</p>
          </li>
        </template>
        <template v-else-if="preview">
          <li v-for="requirement in preview.gapRequirements" :key="requirement" class="prep-question" v-bind="lockedAttrs()">
            <div class="prep-question-text">{{ requirement }}</div>
            <span class="prep-locked mt-1.5">
              <i class="bi" :class="lockIcon" aria-hidden="true" />
              {{ t('interviewPrep.view.gapLocked') }}
              <span v-if="unlock" class="prep-included" :class="unlockHint ? 'opacity-100' : 'opacity-0'" aria-hidden="true">{{ t('interviewPrep.view.included') }}</span>
            </span>
          </li>
        </template>
      </ul>
    </section>

    <section v-if="prep?.askThem.length || preview?.askThemCount">
      <h3 class="prep-heading">{{ t('interviewPrep.view.askThem') }}</h3>
      <ul v-if="prep" class="prep-answer">
        <li v-for="(question, i) in prep.askThem" :key="i">{{ question }}</li>
      </ul>
      <template v-else-if="preview">
        <p class="prep-question prep-locked" v-bind="lockedAttrs()">
          <i class="bi" :class="lockIcon" aria-hidden="true" />
          {{ t('interviewPrep.view.askThemLocked', { n: preview.askThemCount }) }}
          <span v-if="unlock" class="prep-included" :class="unlockHint ? 'opacity-100' : 'opacity-0'" aria-hidden="true">{{ t('interviewPrep.view.included') }}</span>
        </p>
      </template>
    </section>

    <p v-if="prep" class="prep-why">{{ t('interviewPrep.view.reviewNote') }}</p>
  </div>
</template>

<style scoped>
.prep-eyebrow {
  @apply text-xs font-semibold uppercase tracking-wide text-muted dark:text-gray-400;
}

.prep-heading {
  @apply mb-2 font-title !text-base font-semibold !text-ink dark:!text-white;
}

.prep-text {
  @apply !text-sm leading-relaxed !text-body dark:!text-gray-300;
}

.prep-question {
  @apply rounded-lg border border-line bg-white p-3.5 dark:border-gray-700 dark:bg-gray-800;
}

.prep-number {
  @apply flex size-6 shrink-0 items-center justify-center rounded-full bg-ink text-xs font-bold text-white dark:bg-white dark:text-ink;
}

.prep-kind {
  @apply mb-1 inline-block rounded-full px-2 py-0.5 !text-[11px] font-semibold;
}

.prep-question-text {
  @apply block font-title !text-sm font-semibold !text-ink dark:!text-white;
}

.prep-why {
  @apply mb-2 !text-xs italic !text-muted dark:!text-gray-400;
}

.prep-answer {
  @apply list-disc space-y-1 pl-4 !text-sm !text-body dark:!text-gray-300;
}

.prep-based {
  @apply mt-2 flex items-center gap-1.5 !text-xs !text-muted dark:!text-gray-400;
}

.prep-locked {
  @apply flex items-center gap-1.5 !text-xs font-semibold !text-muted dark:!text-gray-400;
}

.prep-more {
  @apply border-dashed border-accent/50 bg-accent/5 dark:bg-accent/10;
}

.prep-unlockable {
  @apply cursor-pointer transition duration-300 focus:outline-none;
}

/* Every locked part at once, while any one of them is pointed at. */
.prep-unlockable-hint {
  @apply border-accent bg-accent/5 ring-2 ring-accent/30 dark:bg-accent/10;
}

.prep-included {
  @apply ml-auto shrink-0 whitespace-nowrap rounded-full bg-ink px-2 py-0.5 !text-[11px] font-semibold !text-white transition-opacity duration-200 dark:bg-white dark:!text-ink;
}

.prep-compact .prep-heading {
  @apply !text-sm;
}

.prep-compact .prep-answer,
.prep-compact .prep-text {
  @apply !text-xs;
}
</style>
